import axiosInstance from "@/config/axios/api-client.config";
import { handleApiRequest } from "@/lib/utils";
import {
  ProductSKUResponse,
  ProductSKUByCategoryResponse,
  ProductSKUFilterResponse,
  ProductSKUFilterRequest,
  GroupAnalyzeResponseData,
  CreateProductSKUPayload,
  UpdateProductSKUPayload,
  BulkCreateProductSKUPayload,
  GroupAnalyzePayload,
} from "@/types/product-sku.types";

/**
 * Fetch a single product SKU by ID
 * GET /product-sku/{brand_id}/{sku_id}
 */
export async function getProductSKU(
  brandId: string,
  skuId: string
): Promise<ProductSKUResponse> {
  return handleApiRequest<ProductSKUResponse>(
    axiosInstance.get(`/product-sku/${brandId}/${skuId}`)
  );
}

/**
 * Fetch all product SKUs for a brand
 * GET /brands/{brand_id}/product-skus
 */
export async function getProductSKUs(
  brandId: string
): Promise<ProductSKUResponse[]> {
  console.log("Fetching product SKUs for brand:", brandId);
  return handleApiRequest<ProductSKUResponse[]>(
    axiosInstance.get(`/product-sku/${brandId}`)
  );
}

/**
 * Update the last_used_at timestamp for a product SKU
 * PATCH /brands/{brand_id}/product-skus/{sku_id}
 */
export async function updateProductSKULastUsed(
  brandId: string,
  skuId: string
): Promise<ProductSKUResponse> {
  return handleApiRequest<ProductSKUResponse>(
    axiosInstance.patch(`/brands/${brandId}/product-skus/${skuId}`)
  );
}

/**
 * Touch a product SKU to update its updated_at timestamp
 * PATCH /product-sku/{brand_id}/{sku_id}/touch
 */
export async function touchProductSKU(
  brandId: string,
  skuId: string
): Promise<ProductSKUResponse> {
  return handleApiRequest<ProductSKUResponse>(
    axiosInstance.patch(`/product-sku/${brandId}/${skuId}/touch`)
  );
}

/**
 * Delete a product SKU
 * DELETE /product-sku/{brand_id}/{sku_id}
 */
export async function deleteProductSKU(
  brandId: string,
  skuId: string
): Promise<void> {
  return handleApiRequest<void>(
    axiosInstance.delete(`/product-sku/${brandId}/${skuId}`)
  );
}

/**
 * Create a new product SKU for a brand
 * POST /product-sku/{brand_id}
 */
export async function createProductSKU(
  brandId: string,
  payload: CreateProductSKUPayload
): Promise<ProductSKUResponse> {
  return handleApiRequest<ProductSKUResponse>(
    axiosInstance.post(`/product-sku/${brandId}`, payload)
  );
}

/**
 * Update product SKU fields (name, sku_code, description, products/angles)
 * PUT /product-sku/{brand_id}/{sku_id}
 */
export async function updateProductSKU(
  brandId: string,
  skuId: string,
  payload: UpdateProductSKUPayload
): Promise<ProductSKUResponse> {
  console.log("Updating product SKU payload:", { payload });
  return handleApiRequest<ProductSKUResponse>(
    axiosInstance.put(`/product-sku/${brandId}/${skuId}`, payload)
  );
}

/**
 * Bulk create product SKUs for a brand
 * POST /product-sku/{brand_id}/bulk-product-sku-upload
 */
export async function bulkCreateProductSKUs(
  brandId: string,
  payload: BulkCreateProductSKUPayload
): Promise<ProductSKUResponse[]> {
  return handleApiRequest<ProductSKUResponse[]>(
    axiosInstance.post(`/product-sku/${brandId}/bulk-product-sku-upload`, payload)
  );
}

/**
 * Analyze and group a set of image URLs into categorised product SKUs
 * POST /product-sku/{brand_id}/group-analyze
 */
export async function groupAnalyzeProductSKU(
  brandId: string,
  payload: GroupAnalyzePayload
): Promise<GroupAnalyzeResponseData> {
  return handleApiRequest<GroupAnalyzeResponseData>(
    axiosInstance.post(`/product-sku/${brandId}/group-analyze`, payload)
  );
}

/**
 * Fetch all categories for a brand's product SKUs
 * GET /product-sku/{brand_id}/categories
 */
export async function getProductSKUCategories(
  brandId: string
): Promise<string[]> {
  return handleApiRequest<string[]>(
    axiosInstance.get(`/product-sku/${brandId}/categories`)
  );
}

/**
 * Fetch product SKUs filtered by category
 * GET /product-sku/{brand_id}/filter-by-category?category={category}
 */
export async function getProductSKUsByCategory(
  brandId: string,
  category: string
): Promise<ProductSKUByCategoryResponse[]> {
  return handleApiRequest<ProductSKUByCategoryResponse[]>(
    axiosInstance.post(`/product-sku/${brandId}/filter-by-category`, { category })
  );
}

/**
 * Filter product SKUs with pagination
 * POST /product-sku/{brand_id}/filter
 */
export async function filterProductSKUs(
  brandId: string,
  payload: ProductSKUFilterRequest
): Promise<ProductSKUFilterResponse[]> {
  return handleApiRequest<ProductSKUFilterResponse[]>(
    axiosInstance.post(`/product-sku/${brandId}/filter`, payload)
  );
}
